import axios from "axios";
import { jwtDecode } from "jwt-decode";

const baseURL = process.env.REACT_APP_API_BASE_URL;
const SITUACOES_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const SITUACOES_CACHE_PREFIX = "corretor_situacoes_cvcrm:v1";
const situacoesMemoryCache = new Map();
const situacoesPendingRequests = new Map();
const whatsappStatusMemoryCache = new Map();
const whatsappStatusPendingRequests = new Map();

const getCorretorAuthContext = () => {
  const rawToken =
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("token");

  if (!rawToken) {
    throw new Error("Token nao encontrado.");
  }

  const token = rawToken.startsWith("Bearer ") ? rawToken.slice(7) : rawToken;
  const decoded = jwtDecode(token);

  return {
    token,
    idClienteAipim:
      decoded?.id_cliente ||
      decoded?.idCliente ||
      decoded?.idClienteAipim ||
      decoded?.cliente_id ||
      null,
    role: decoded?.role || null,
    idUsuario: decoded?.id_usuario || decoded?.idUsuario || decoded?.sub || null,
  };
};

const getSituacoesCacheKey = ({ idClienteAipim, idUsuario }) =>
  `${SITUACOES_CACHE_PREFIX}:${idClienteAipim || idUsuario || "sessao"}`;

const getCachedSituacoes = (cacheKey) => {
  const inMemory = situacoesMemoryCache.get(cacheKey);
  if (inMemory?.expiresAt > Date.now()) return inMemory.data;

  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
    if (!Array.isArray(cached?.data) || cached.expiresAt <= Date.now()) return null;
    situacoesMemoryCache.set(cacheKey, cached);
    return cached.data;
  } catch (error) {
    return null;
  }
};

const cacheSituacoes = (cacheKey, data) => {
  const cached = { data, expiresAt: Date.now() + SITUACOES_CACHE_TTL_MS };
  situacoesMemoryCache.set(cacheKey, cached);

  try {
    localStorage.setItem(cacheKey, JSON.stringify(cached));
  } catch (error) {
    // O cache em memoria continua disponivel quando o navegador bloqueia o armazenamento local.
  }
};

const getApiErrorMessage = (err, fallbackMessage) => {
  const responseData = err?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) return responseData;
  if (responseData?.message) return responseData.message;
  if (responseData?.erro) return responseData.erro;
  if (responseData?.detail) return responseData.detail;

  return err?.message || fallbackMessage;
};

const getDashboard = async () => {
  const { token } = getCorretorAuthContext();

  const response = await axios.get(`${baseURL}/produto/AV/corretor/dashboard`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response?.data;
};

const getMensagens = async ({ idCorretor, limite = 200 } = {}) => {
  const { token, idClienteAipim } = getCorretorAuthContext();

  if (!idClienteAipim) {
    throw new Error("idClienteAipim nao encontrado no token.");
  }

  if (!idCorretor) {
    throw new Error("id_Corretor nao encontrado para carregar mensagens.");
  }

  const response = await axios.get(`${baseURL}/produto/AV/corretor/mensagens`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      idClienteAipim,
      id_Corretor: idCorretor,
      limite,
    },
  });

  return Array.isArray(response?.data) ? response.data : [];
};

const getLeads = async ({
  idCorretor,
  busca,
  idSituacao,
  ativo = true,
  limit = 20,
  offset = 0,
} = {}) => {
  const { token, idClienteAipim } = getCorretorAuthContext();

  if (!idClienteAipim) {
    throw new Error("idClienteAipim nao encontrado no token.");
  }

  if (!idCorretor) {
    throw new Error("id_Corretor nao encontrado para carregar leads.");
  }

  const params = {
    idClienteAipim,
    id_Corretor: idCorretor,
    ativo,
    limit,
    offset,
  };

  if (busca) params.busca = busca;
  if (idSituacao) {
    params.idSituacao = idSituacao;
    // O endpoint do portal usa camelCase, enquanto integracoes CVCRM usam idsituacao.
    params.idsituacao = idSituacao;
  }

  const response = await axios.get(`${baseURL}/produto/AV/corretor/leads`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });

  return response?.data || { limit, offset, totalRetornado: 0, itens: [] };
};

const getLeadDetails = async ({ idLead } = {}) => {
  const { token } = getCorretorAuthContext();

  if (!idLead) {
    throw new Error("ID do lead nao informado para carregar os detalhes.");
  }

  const response = await axios.get(
    `${baseURL}/produto/AV/corretor/leads/${encodeURIComponent(idLead)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response?.data || null;
};

const getWhatsappStatus = async ({ forceRefresh = false } = {}) => {
  const { token, idClienteAipim, idUsuario } = getCorretorAuthContext();
  const cacheKey = `${idClienteAipim || "sem-cliente"}:${idUsuario || "sem-usuario"}`;

  if (!forceRefresh && whatsappStatusMemoryCache.has(cacheKey)) {
    return whatsappStatusMemoryCache.get(cacheKey);
  }

  const pendingRequest = whatsappStatusPendingRequests.get(cacheKey);
  if (pendingRequest) return pendingRequest;

  const request = axios
    .get(`${baseURL}/aidirect/whatsapp/status`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        produtoOrigem: "CORR",
      },
    })
    .then((response) => {
      const data = response?.data || { configurado: false, qualidadeConta: "NOT_AVAILABLE" };
      whatsappStatusMemoryCache.set(cacheKey, data);
      return data;
    })
    .finally(() => {
      whatsappStatusPendingRequests.delete(cacheKey);
    });

  whatsappStatusPendingRequests.set(cacheKey, request);
  return request;
};

const getSituacoes = async () => {
  const { token, idClienteAipim, idUsuario } = getCorretorAuthContext();
  const cacheKey = getSituacoesCacheKey({ idClienteAipim, idUsuario });
  const cached = getCachedSituacoes(cacheKey);
  if (cached) return cached;

  const pendingRequest = situacoesPendingRequests.get(cacheKey);
  if (pendingRequest) return pendingRequest;

  const request = axios
    .get(`${baseURL}/produto/AV/corretor/situacoes`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      const data = Array.isArray(response?.data) ? response.data : [];
      cacheSituacoes(cacheKey, data);
      return data;
    })
    .finally(() => {
      situacoesPendingRequests.delete(cacheKey);
    });

  situacoesPendingRequests.set(cacheKey, request);
  return request;
};

const corretorDashboardService = {
  getCorretorAuthContext,
  getApiErrorMessage,
  getDashboard,
  getMensagens,
  getLeads,
  getLeadDetails,
  getWhatsappStatus,
  getSituacoes,
};

export default corretorDashboardService;
